import { COMPANY_STATUS } from 'helper/constant';
import { capitalize } from 'lodash';

import { ReactComponent as Resend } from 'asset/IconsPack/at_mail-01.svg';
import { ReactComponent as Role } from 'asset/IconsPack/at_user-01.svg';
import { ReactComponent as Edit } from 'asset/IconsPack/at_user-edit.svg';
import { ReactComponent as Reactivate } from 'asset/IconsPack/at_user-plus-02.svg';
import { ReactComponent as Suspend } from 'asset/IconsPack/at_user-x-02.svg';

const env = capitalize(process.env.REACT_APP_BUILD_ENV);

// Values of company.meta_data.agreement_status (PandaDoc customer-agreement flow).
export const AGREEMENT_STATUS = {
    INITIATED: 'initiated',
    AWAITING_USER: 'awaiting_user',
    AWAITING_ADMIN: 'awaiting_admin',
    APPROVED: 'approved',
};

// Pending state sub stages to show which pending state exactly they are in
export const ACCOUNT_PENDING_STAGE = {
    AGREEMENT_UNSIGNED: 'agreementUnsigned',
    AGREEMENT_IN_REVIEW: 'agreementInReview',
    PAYMENT: 'payment',
};

export const ACCOUNT_STATUS = {
    [COMPANY_STATUS.APPROVED]: { label: 'Approved' },
    [COMPANY_STATUS.REJECTED]: { label: 'Rejected' },
    [COMPANY_STATUS.REVOKED]: { label: 'Revoked', subStatus: 'Insufficient Balance' },
    [COMPANY_STATUS.PENDING]: {
        label: 'Pending',
        subStatusByStage: {
            [ACCOUNT_PENDING_STAGE.AGREEMENT_UNSIGNED]: 'Agreement Due',
            [ACCOUNT_PENDING_STAGE.AGREEMENT_IN_REVIEW]: 'Account Under Review',
            [ACCOUNT_PENDING_STAGE.PAYMENT]: 'Account Approved, Add Balance',
        },
    },
};

export const CUSTOM_SENDER_EMAIL = {
    TOOL_TIP_CONTENT:
        'You will need to verify your access to the email address. Once verified, the address can be used to send emails to your customers when sending a verification link, or any other customer-facing communication.',
    DEFAULT_DESCRIPTION:
        "Choose the email address you'd like to use for all user verification communications.",
};

export const WEBHOOK_NOTIFICATION_TOOLTIP =
    'When enabled, webhook requests include an Authorisation header with a Bearer API key.';

export const ADMIN_OPTIONS_ICONS = {
    changeRole: <Role />,
    updateMember: <Edit />,
    suspend: <Suspend />,
    resendInvitation: <Resend />,
    reActivate: <Reactivate />,
};

export const teamMemberModalTitle = {
    resendInvitation: 'Resend invitation',
    reActivate: 'Re-activate member',
    suspend: 'Suspend member?',
    createMember: `Add Member to ${env}`,
    updateMember: 'Update member details',
    changeRole: 'Change member role',
};

export const userPermissionsTableHeader = [
    { _id: 1, name: 'Categories' },
    { _id: 2, name: 'Owner' },
    { _id: 3, name: 'Admin' },
    { _id: 4, name: 'Viewer' },
];

// Permission data structure
export const usersPermissionsData = {
    categories: [
        {
            name: 'Billing & Usage',
            subcategories: [],
            permissions: {
                owner: ['Edit', 'View'],
                admin: [],
                viewer: [],
            },
        },
        {
            name: 'Integrations',
            subcategories: [],
            permissions: {
                owner: ['Edit', 'View'],
                admin: ['Edit', 'View'],
                viewer: ['View'],
            },
        },
        {
            name: 'Medallion™',
            subcategories: [],
            permissions: {
                owner: ['Edit', 'View'],
                admin: ['Edit', 'View'],
                viewer: ['View'],
            },
        },
        {
            name: 'User Management',
            subcategories: [
                {
                    name: 'General',
                    permissions: {
                        owner: ['Edit', 'View'],
                        admin: ['Edit', 'View'],
                        viewer: ['View'],
                    },
                },
                {
                    name: 'True Continuous Monitoring™',
                    permissions: {
                        owner: ['Edit', 'View'],
                        admin: ['Edit', 'View'],
                        viewer: ['View'],
                    },
                },
                {
                    name: 'Events',
                    permissions: {
                        owner: ['View'],
                        admin: ['View'],
                        viewer: ['View'],
                    },
                },
                {
                    name: 'Reports',
                    permissions: {
                        owner: ['View'],
                        admin: ['View'],
                        viewer: ['View'],
                    },
                },
            ],
            permissions: {},
        },
        {
            name: 'API & SDK',
            subcategories: [
                {
                    name: 'API Credentials',
                    permissions: {
                        owner: ['View'],
                        admin: ['View'],
                        viewer: [],
                    },
                },
                {
                    name: 'Low Code SDK',
                    permissions: {
                        owner: ['Edit', 'View'],
                        admin: ['Edit', 'View'],
                        viewer: [],
                    },
                },
            ],
            permissions: {},
        },
        {
            name: 'Settings & Configuration',
            subcategories: [
                {
                    name: 'Custom Email',
                    permissions: {
                        owner: ['Edit', 'View'],
                        admin: ['Edit', 'View'],
                        viewer: [],
                    },
                },
                {
                    name: 'Webhooks',
                    permissions: {
                        owner: ['Edit', 'View'],
                        admin: ['Edit', 'View'],
                        viewer: [],
                    },
                },
                {
                    name: 'Webhook delivery logs',
                    permissions: {
                        owner: ['View'],
                        admin: ['View'],
                        viewer: [],
                    },
                },
                {
                    name: 'Team Management',
                    permissions: {
                        owner: ['Edit', 'View'],
                        admin: ['View'],
                        viewer: ['View'],
                    },
                },
            ],
            permissions: {},
        },
    ],
};

export const MAX_WEBHOOKS = 15;

export const WEBHOOK_TABLE_HEADERS = ['Webhook URL', 'Status', 'Auth token', ''];

export const MULTIPLE_WEBHOOKS_COPIES = {
    EMPTY_HEADING: 'No webhooks added yet',
    EMPTY_SUBTEXT: `Add a webhook to start receiving real-time event notifications. You can configure up to ${MAX_WEBHOOKS} webhooks.`,
    LIMIT_BANNER: `You've reached the maximum of ${MAX_WEBHOOKS} webhooks. Please delete an existing webhook before adding a new one.`,
    STATUS_TOOLTIP: 'Toggle to enable or disable this webhook from receiving event notifications.',
    RESTRICTED_TOOLTIP: 'Webhooks can be managed once your account is active.',
    REGEN_INACTIVE_TOOLTIP: 'Webhook must be active to regenerate the API key',
    ADD_SUCCESS: 'Webhook added.',
    ADD_ERROR: "We couldn't add this webhook. Check the URL and try again.",
    UPDATE_ERROR: "We couldn't save your changes. Try again.",
    UPDATE_SUCCESS: 'Webhook URL updated.',
    DELETE_SUCCESS: 'Webhook deleted.',
    TURN_ON_SUCCESS: 'Webhook turned on.',
    TURN_OFF_SUCCESS: 'Webhook turned off.',
    TOGGLE_ERROR: 'Failed to update webhook status.',
    LOAD_ERROR_TITLE: "We couldn't load your webhooks",
    LOAD_ERROR_BODY:
        'Your webhooks are not affected and keep receiving events. Try again in a moment.',
    GENERIC_ERROR: 'Something went wrong.',
    REGEN_SUCCESS: 'API key regenerated successfully.',
    REGEN_KEY_COPY_SUCCESS: 'New API key copied to clipboard.',
    REGEN_PASSWORD_TITLE: 'Regenerate API Key',
    REGEN_PASSWORD_BODY: 'Enter your password to regenerate the API key.',
    REGEN_PASSWORD_SUBMIT: 'Regenerate',
};

export const WEBHOOK_LOADING_KEYS = {
    CREATE: 'createWebhook',
    UPDATE: 'updateWebhook',
    DELETE: 'deleteWebhook',
    FETCH_TOKEN: 'fetchWebhookToken',
    REGENERATE_TOKEN: 'regenerateWebhookToken',
};

export const WEBHOOK_MODAL_TYPE = {
    ADD: 'add',
    EDIT: 'edit',
    DELETE: 'delete',
    PASSWORD: 'password',
    REGEN_CONFIRM: 'regenConfirm',
    REGEN_PASSWORD: 'regenPassword',
    SHOW_API_KEY: 'showApiKey',
    TURN_OFF: 'turnOff',
};

export const NOTIFICATION_ROLE_ACCESS = {
    mandatory: 'mandatory',
    optional: 'optional',
    disabled: 'disabled',
};

export const NOTIFICATION_SETTINGS = [
    {
        notificationType: 'medallion',
        preferenceKey: 'medallion',
        title: 'Medallion™',
        toastLabel: 'Medallion™',
        roleAccess: {
            Owner: NOTIFICATION_ROLE_ACCESS.optional,
            Admin: NOTIFICATION_ROLE_ACCESS.optional,
            Viewer: NOTIFICATION_ROLE_ACCESS.optional,
        },
    },
    {
        notificationType: 'user_monitoring_alerts',
        preferenceKey: 'true_continuos_monitoring',
        title: 'User Monitoring Alerts',
        toastLabel: 'User Monitoring',
        roleAccess: {
            Owner: NOTIFICATION_ROLE_ACCESS.optional,
            Admin: NOTIFICATION_ROLE_ACCESS.optional,
            Viewer: NOTIFICATION_ROLE_ACCESS.optional,
        },
    },
    {
        notificationType: 'account_alerts',
        preferenceKey: 'account',
        title: 'Account Alerts',
        toastLabel: 'Account',
        roleAccess: {
            Owner: NOTIFICATION_ROLE_ACCESS.mandatory,
            Admin: NOTIFICATION_ROLE_ACCESS.optional,
            Viewer: NOTIFICATION_ROLE_ACCESS.disabled,
        },
        tooltipMessages: {
            mandatory: 'Account alerts are required for Owners and are always enabled.',
            disabled: 'You don’t have permission to modify this setting.',
        },
    },
];
